/**
 * Tanjong Life · "Build the claims payment authorisation process by describing it."
 *
 * tl-pr14 is ALREADY on the shelf (Deployed v2 — PMT-3305 is sitting at its first gate).
 * The conversation assembles it from an outline that mirrors the seeded doc (the test pins
 * the mirror), written so the room watches the control arrive rather than being told about
 * it: the second gate lands only after the presenter has answered who releases, and the
 * payee re-read lands with it, immediately after the Wizard names the window it closes.
 * It ends by opening the deployed process rather than deploying a second copy.
 *
 * Keywords are compounded on `settlement`, `payee`, `dual control` or `payment
 * authorisation`; none carries a bare `claim`.
 */
import type { Scenario } from '../director.js';
import { slicer } from './tanjong-canvas.js';
import { MAKE_PAYMENT } from './tanjong-make-intents.js';
import { PAYMENT_DOC as DOC } from './tanjong-outline-payment.js';

const S = slicer(DOC);
const P_NAME = S.slice({ name: DOC.name, agent: DOC.agent });
const P_TRIGGERS = S.slice({ triggers: DOC.triggers });
const P_STAGE1 = S.stagePatch(0);
const P_STAGE2 = S.stagePatch(1);
const P_STAGE3 = S.stagePatch(2);
// The first gate lands alone. The second desk, and the re-read between them, arrive only
// after the presenter has said who releases — the room watches dual control being chosen.
const s4 = S.stageAt(3);
const [gate1, ...afterGate1] = S.nodesIn(s4.id);
const P_GATE1 = S.slice({ stages: [s4], nodes: gate1 ? [gate1] : [] });
const P_GATE2 = S.slice({ nodes: afterGate1 });
const P_STAGE5 = { ...S.stagePatch(4), objective: DOC.objective ?? null, metrics: DOC.metrics ?? [] };

export const tanjongBuildPayment: Scenario = {
  id: 'tanjong-build-payment',
  orgId: 'tanjong-life',
  keywords: [
    'payment authorisation',
    'payment authorization',
    'settlement authorisation',
    'authorise a settlement',
    'dual control',
    'payee screening',
    'release a payment',
    'settlement instruction',
  ],
  intents: [MAKE_PAYMENT],
  steps: [
    {
      do: 'say',
      text: 'This one starts after the claim has been decided, so nothing here adjudicates. What it has to get right is the money and the payee — and who is allowed to say yes.',
      delay: 500,
    },
    {
      do: 'canvas',
      patch: P_NAME,
      summary: 'Named it Claims payment authorisation · Settlement Assurance Sentinel set as the driver',
      delay: 700,
    },
    {
      do: 'canvas',
      patch: P_TRIGGERS,
      summary: 'Added 2 triggers — claim adjudicated · bank return file (re-enters at payee screening)',
      delay: 600,
    },
    {
      do: 'canvas',
      patch: P_STAGE1,
      summary: 'Stage 1 · Assemble the instruction — 4 activities',
      delay: 800,
    },
    {
      do: 'say',
      text: 'The instruction is assembled from the adjudication rather than retyped from it. A number typed twice is a number that can differ, and this is the one workflow where a mistake does not come back.',
      delay: 800,
    },
    {
      do: 'ask',
      id: 'recon',
      prompt: 'How closely should the settlement match the adjudication?',
      options: [
        { id: 'cent', label: 'To the cent (recommended)' },
        { id: 'tol', label: 'Within a small tolerance' },
      ],
      delay: 600,
    },
    {
      do: 'branch',
      cases: {
        tol: [
          {
            do: 'say',
            text: 'A tolerance is a policy nobody wrote down, and it is the number an auditor will ask you to justify. I will reconcile to the cent and halt on a mismatch — you can widen it per benefit later if the desk wants.',
            delay: 700,
          },
        ],
      },
      else: [
        {
          do: 'say',
          text: 'To the cent. SGD 12,950 less the SGD 100 deposit is SGD 12,850, or it stops and goes to the claims manager — it is not rounded and it is not released pending investigation.',
          delay: 600,
        },
      ],
    },
    {
      do: 'canvas',
      patch: P_STAGE2,
      summary: 'Stage 2 · Reconcile to the adjudication — 3 activities',
      delay: 800,
    },
    {
      do: 'insight',
      title: 'Both payee facts already exist in your records',
      body: 'The receiving account and its change history are on the claim today, and so is the account name. Neither is new information. On the run this replaces they were simply never read together before the money moved — the account had been changed nine days before the claim was submitted, and the name did not match the life assured.',
      sources: ['Claims administration · payee change history'],
      delay: 1100,
    },
    {
      do: 'canvas',
      patch: P_STAGE3,
      summary: 'Stage 3 · Payee screening — 5 activities · milestone M1',
      delay: 850,
    },
    {
      do: 'say',
      text: 'Read the note that step produces. It states both facts with their dates and says plainly that neither is proof of anything on its own. An approver told “fraud detected” learns less than one told what changed and when. And the account number is masked in every trace this stage leaves.',
      delay: 900,
    },
    {
      do: 'askText',
      id: 'authoriser',
      prompt: 'Who authorises a settlement?',
      placeholder: 'e.g. the Claims Managers',
      delay: 650,
    },
    { do: 'canvas', patch: P_GATE1, summary: 'Stage 4 · Dual control — first gate added', delay: 800 },
    {
      do: 'ask',
      id: 'release',
      prompt: 'And who releases the money?',
      options: [
        { id: 'treasury', label: 'A separate treasury desk (recommended)' },
        { id: 'same', label: 'The same claims manager' },
        { id: 'timeout', label: 'Release automatically if nobody objects' },
      ],
      delay: 700,
    },
    {
      do: 'branch',
      cases: {
        same: [
          {
            do: 'say',
            text: 'Then it is not dual control — it is one signature written twice. I will wire treasury as the second desk and add a step that asserts the two approvers are different people, because on the run this replaces the second approver was on leave and it went out on one.',
            delay: 900,
          },
        ],
        timeout: [
          {
            do: 'say',
            text: 'I would refuse that one. A payment that goes out because everybody was busy is exactly the failure this workflow exists to prevent. There is no timeout on this gate.',
            delay: 900,
          },
        ],
      },
      else: [
        {
          do: 'say',
          text: 'Treasury, on a different desk. And no timeout — if neither person acts, nothing is released.',
          delay: 600,
        },
      ],
    },
    {
      do: 'insight',
      title: 'And one step you did not ask for, between the two gates',
      body: 'Your workflow screens the payee when it assembles the instruction, and never again. Between a settlement being authorised and being released, nobody re-reads the receiving account — so an account changed in that window goes out unexamined. I am adding a step that re-reads it after authorisation. It is the narrowest gap in this act and it is the first thing the Claims Integrity Sentinel was built to watch.',
      sources: ['Claims administration · payee change history', 'Claims Integrity Sentinel · probe two'],
      delay: 1200,
    },
    {
      do: 'canvas',
      patch: P_GATE2,
      summary:
        'Payee re-read after authorisation · second gate on a separate desk · dual-control assertion',
      delay: 900,
    },
    {
      do: 'canvas',
      patch: P_STAGE5,
      summary: 'Stage 5 · Release & record — 3 activities · milestone M2',
      delay: 800,
    },
    {
      do: 'say',
      text: 'Nineteen activities across five stages, and exactly one of them moves money — guarded on treasury’s decision, and held by no agent. Testing the steps now.',
      delay: 850,
    },
    {
      do: 'test',
      title: 'Testing components…',
      items: [
        { label: 'Claims administration trigger', sub: 'Adjudicated claim fires & maps the reference' },
        { label: 'Instruction assembly', sub: 'Built from the adjudication, never retyped' },
        { label: 'Amount reconciliation', sub: 'Instruction and adjudication agree to the cent' },
        { label: 'Payee read', sub: 'Receiving account & change history resolve · account masked' },
        { label: 'Look-back window', sub: '30 days before submission flags, and does not conclude' },
        { label: 'Name comparison', sub: 'Account name against the life assured' },
        {
          label: 'Payee screening',
          sub: 'Sanctions & watchlist read · no write before the second gate',
        },
        { label: 'First gate', sub: 'Claims Managers routing · 8-hour target' },
        { label: 'Payee re-read', sub: 'Runs after authorisation, before release' },
        { label: 'Second gate', sub: 'Treasury & Release Authority · no releasing timeout' },
        { label: 'Dual-control assertion', sub: 'The two approvers must be different people' },
        {
          label: 'Disbursements write',
          sub: 'Refused for any agent identity · one write, after two approvals',
        },
      ],
      result: 'All components passed',
      delay: 600,
    },
    {
      do: 'say',
      text: 'All checks passed. There is no path through this process that releases money on one signature, and no path that skips the re-read.',
      delay: 7400,
    },
    {
      do: 'say',
      text: 'This one is already on the shelf as v2 — and PMT-3305 is sitting at its first gate right now: SGD 12,850, a payee name that does not match the life assured, an account changed nine days before submission. Nothing about that is proof. It is why a person is being asked.',
      delay: 900,
    },
    {
      do: 'link',
      label: 'Open Claims payment authorisation — already deployed, v2',
      href: '/processes/tl-pr14',
      delay: 600,
    },
  ],
};
